// ── Step3 Entry — init + generateAll orchestration ───────────────
// 编排层；state 读取待批次 I 迁 store

import {
  getState,
  setState,
  setStateBulk,
} from '../../../state.js';
import { createGenerationProgress } from '../../../ui/generation-progress.js';
import {
  startGenerateAll,
  pollGenerateAll,
  getActiveGenerateAll,
  getListingGenerationQueue,
  retrySearchTeamOnly,
} from '../../../step3/api.js';
import { scheduleStep3AutoSave } from '../../../step3/autosave.js';
import {
  resolveSnapshotAsinsForApi,
} from '../../../listing-utils.js';
import { applyGenerateAllResult } from '../../../step3/generation-result.js';
import {
  $,
  getBatchSnapshotsList,
  sectionLabel,
} from './vm.js';
import {
  updateChecklist,
} from './render.js';
import { bindEvents, bindStateSubscriptions } from './bindings.js';
import { buildListingResultViewModel } from '../../../view-models/listing-result.viewmodel.js';
import { showToast } from '../../../toast.js';
import {
  formatListingGenerationError,
  isTerminalListingGenerationTaskError,
} from '../../../step3/generation-error.js';
import {
  applySearchTeamRetryResult,
  resolveSearchTeamRetryAvailability,
} from '../../../step3/search-team-retry.js';

const LISTING_PHASE_LABELS = {
  init: '初始化上下文',
  running: '生成中',
  'single-pass': 'MiniMax 全量生成',
  title: '生成标题',
  bullets: '生成五点',
  qa: '生成问答',
  description: '生成商品描述',
  searchTerms: '生成搜索词',
  optimizer: 'AI 优化诊断',
  'v2-title': 'V2 标题校正',
  'v2-bullets': 'V2 五点校正',
  'v2-description': 'V2 描述校正',
  finalize: '收尾汇总',
  completed: '已完成',
  failed: '生成失败',
};

function formatQueueHint(stats = {}) {
  const running = Number(stats.running || 0);
  const waiting = Number(stats.waiting || 0);
  const maxRunning = Number(stats.maxRunning || 0);
  const maxQueue = Number(stats.maxQueue || 0);
  const availableRunningSlots = Number.isFinite(Number(stats.availableRunningSlots))
    ? Number(stats.availableRunningSlots)
    : Math.max(0, maxRunning - running);
  const availableQueueSlots = Number.isFinite(Number(stats.availableQueueSlots))
    ? Number(stats.availableQueueSlots)
    : Math.max(0, maxQueue - waiting);

  if (maxRunning > 0 && availableRunningSlots > 0 && waiting === 0) {
    return `可立即生成 ${availableRunningSlots}/${maxRunning}`;
  }
  if (availableQueueSlots <= 0) {
    return `队列已满 ${waiting}/${maxQueue}`;
  }
  return `运行 ${running}/${maxRunning || '-'} · 等待 ${waiting}/${maxQueue || '-'}`;
}

function renderListingQueueHint(stats, { error = false } = {}) {
  const hint = $('#listing-queue-slot-hint');
  if (!hint) return;
  hint.textContent = error ? '队列状态暂不可用' : formatQueueHint(stats);
  hint.classList.toggle('is-error', error);
}

async function refreshListingQueueHint() {
  try {
    const stats = await getListingGenerationQueue();
    renderListingQueueHint(stats);
    return stats;
  } catch (err) {
    renderListingQueueHint(null, { error: true });
    return null;
  }
}

function formatSeconds(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

function getListingProgress(st = {}) {
  return st.progress || st.task?.progress || st.task?.task?.progress || {};
}

function getQueueWaitText(queue = {}) {
  if (!queue || queue.waiting <= 0) return '';
  return `，队列等待 ${queue.waiting}`;
}

function resolveGenerationText(st, startedAt) {
  const phase = st?.phase
    || getListingProgress(st)?.phase
    || st?.task?.phase
    || st?.task?.task?.phase
    || st?.status
    || 'running';
  const normalizedPhase = String(phase || '').trim();
  const phaseLabel = LISTING_PHASE_LABELS[normalizedPhase] || normalizedPhase || '生成中';
  const progress = getListingProgress(st);
  const total = Number(progress.total) || 0;
  const completed = Number(progress.completed) || 0;
  const message = String(progress.message || '').trim();
  const progressHint = total > 0 ? `${completed}/${total} 步` : '';
  const elapsedHint = `已用时 ${formatSeconds(Date.now() - startedAt)}`;
  const detail = [message, progressHint, elapsedHint].filter(Boolean).join(' · ');
  return {
    phaseLabel,
    detail,
  };
}

const LISTING_PROGRESS_STEPS = [
  { key: 'queued', label: '排队' },
  { key: 'submit', label: '提交' },
  { key: 'generate', label: '生成' },
  { key: 'done', label: '写入' },
];

function renderListingProgress({ title, detail, footer, phase = 'generate', tone = 'running', percent = 18 } = {}) {
  const mount = document.getElementById('step3-generation-progress');
  if (!mount) return;
  mount.closest('.tab-content-container')?.classList.add('is-generating-listing');
  mount.classList.add('is-visible');
  mount.replaceChildren(createGenerationProgress({
    title,
    detail,
    footer,
    phase,
    tone,
    percent,
    steps: LISTING_PROGRESS_STEPS,
  }));
}

function hideListingProgress(delay = 0) {
  const mount = document.getElementById('step3-generation-progress');
  if (!mount) return;
  window.setTimeout(() => {
    mount.closest('.tab-content-container')?.classList.remove('is-generating-listing');
    mount.classList.remove('is-visible');
    mount.replaceChildren();
  }, Math.max(0, Number(delay) || 0));
}

function listingProgressPercent(st = {}, startedAt = Date.now()) {
  const status = String(st.status || '').toLowerCase();
  if (status === 'queued') return 16;
  const progress = getListingProgress(st);
  const total = Number(progress.total) || 0;
  const completed = Number(progress.completed) || 0;
  if (total > 0) return Math.min(88, 24 + Math.round((completed / total) * 58));
  return Math.min(84, 36 + Math.floor((Date.now() - startedAt) / 30000) * 6);
}

// ── Generate All ─────────────────────────────────────────────────

let searchTeamRetryInFlight = false;

async function retrySearchTeamFromTask() {
  if (searchTeamRetryInFlight) return;
  const jobId = String(getState('batchJobId') || '');
  const availability = resolveSearchTeamRetryAvailability({
    searchTerms: getState('listingDraftSearchTerms'),
    generationMeta: getState('listingGenerationMeta'),
  });
  if (!jobId || !availability.visible || !availability.taskId) return;

  const taskId = availability.taskId;
  const button = $('#retry-search-team-button');
  searchTeamRetryInFlight = true;
  if (button) {
    button.disabled = true;
    button.textContent = '正在单独补词…';
  }
  try {
    const response = await retrySearchTeamOnly(jobId, taskId);
    if (String(getState('batchJobId') || '') !== jobId) return;
    const applied = applySearchTeamRetryResult(response, {
      jobId,
      taskId,
      getState,
      setStateBulk,
      scheduleAutoSave: scheduleStep3AutoSave,
    });
    if (response?.status === 'degraded') {
      showToast('市场补词仍未完成，已保留原有安全 Search Terms。', {
        id: 'listing-search-team-retry-toast',
        type: 'info',
        duration: 4200,
      });
    } else if (response?.status === 'insufficient') {
      showToast(applied.applied
        ? '已补充可安全接纳的市场词，但当前后台词量仍偏少。'
        : '本轮未发现更多可安全接纳的市场词，已保留当前结果。', {
        id: 'listing-search-team-retry-toast',
        type: 'info',
        duration: 4200,
      });
    } else {
      showToast(applied.applied ? 'Search Terms 市场补词已完成。' : 'Search Terms 已复核，当前安全词无需替换。', {
        id: 'listing-search-team-retry-toast',
        type: 'success',
        duration: 3200,
      });
    }
  } catch (error) {
    if (String(getState('batchJobId') || '') !== jobId) return;
    console.warn('[Step3] SearchTeam-only retry failed; existing Search Terms were preserved.', error);
    showToast(`单独补词未完成：${error?.message || '请稍后重试'}。原有 Search Terms 未变。`, {
      id: 'listing-search-team-retry-toast',
      type: 'error',
      duration: 5000,
      maxWidth: 420,
    });
  } finally {
    searchTeamRetryInFlight = false;
    if (button?.isConnected) {
      button.disabled = false;
      button.textContent = '单独重试市场补词';
    }
  }
}

async function generateAllFromBatch() {
  let submittedTaskId = '';
  let clearSubmittedTask = false;
  const jobId = getState('batchJobId');
  if (!jobId) {
    showToast('请先完成 Step 1 批量分析，再生成 Listing', { id: 'step3-listing-toast', type: 'error' });
    return;
  }

  const btn = $('#generate-all-button');
  if (btn?.disabled) return;
  await refreshListingQueueHint();
  if (btn) {
    btn.disabled = true;
    btn.textContent = '生成中...';
  }

  // Quick checklist update
  const vm = buildListingResultViewModel({
    title: getState('listingDraftTitle'),
    bullets: getState('listingDraftBullets'),
    description: getState('listingDraftDescription'),
    searchTerms: getState('listingDraftSearchTerms'),
    qa: getState('listingDraftQa'),
    selectedTitleVersion: getState('selectedTitleVersion'),
    listingDraftV1: getState('listingDraftV1'),
    generationMeta: getState('listingGenerationMeta'),
    sectionErrors: getState('listingGenerationSectionErrors'),
  });
  updateChecklist(vm);

  const snapshotAsins = resolveSnapshotAsinsForApi(
    getState('batchSnapshots'),
    getState('competitorAsinsIncluded')
  );
  const competitorList = getBatchSnapshotsList();
  if (competitorList.length > 1 && (!snapshotAsins || snapshotAsins.length === 0)) {
    showToast('请至少勾选一个竞品 ASIN 再生成 Listing', { id: 'step3-listing-toast', type: 'error' });
    if (btn) {
      btn.disabled = false;
      btn.textContent = '生成 Listing';
    }
    return;
  }

  const loadingHint = snapshotAsins?.length > 1
    ? `正在综合 ${snapshotAsins.length} 个竞品生成 Listing（标题/五点/描述/搜索词），预计 30-90 秒...`
    : '正在生成 Listing（标题/五点/描述/搜索词），预计 30-60 秒...';

  const progressContainer = $('#step3-generation-progress');
  if (progressContainer) {
    renderListingProgress({
      title: '正在生成 Listing',
      detail: loadingHint,
      footer: '系统会自动轮询生成任务，完成后写入标题、五点、描述和搜索词。',
      phase: 'submit',
      percent: 22,
    });
  }

  try {
    const startedAt = Date.now();
    const start = await startGenerateAll(jobId, {
      masterInstructions: getState('masterRevisionInstructions') || {},
      snapshotAsins,
      keywordUsePolicies: getState('keywordUsePolicies') || [],
      selectedStKeywords: [...(getState('selectedStKeywords') || new Set())],
      competitorRoles: getState('competitorRoles') || [],
      newProductProjectId: getState('newProductProjectId') || null,
    });
    if (start?.batchJobId && String(start.batchJobId) !== String(jobId)) {
      throw Object.assign(new Error('生成任务与当前商品任务不匹配，已停止写回。'), {
        code: 'LISTING_GENERATION_SCOPE_MISMATCH',
        status: 'scope_mismatch',
        taskId: start.taskId || '',
      });
    }

    let data;
    if (start.taskId) {
      submittedTaskId = start.taskId;
      setState('activeListingGenerationTaskId', start.taskId);
      data = await pollGenerateAll(start.taskId, {
        expectedBatchJobId: jobId,
        onProgress(st) {
          if (
            getState('activeListingGenerationTaskId') !== start.taskId
            || String(getState('batchJobId') || '') !== String(jobId)
          ) return;
          const q = st.queue || {};
          const queueHint = getQueueWaitText(q);
          const { phaseLabel, detail } = resolveGenerationText(st, startedAt);
          if (st.status === 'queued') {
            renderListingProgress({
              title: 'Listing 任务排队中',
              detail: `${loadingHint}（排队中${queueHint}）`,
              footer: detail || '等待进入生成队列',
              phase: 'queued',
              percent: listingProgressPercent(st, startedAt),
            });
            return;
          }
          renderListingProgress({
            title: `正在生成 Listing（${phaseLabel}）`,
            detail: detail || `阶段：${phaseLabel}`,
            footer: '标题、五点、描述和搜索词会按后台结果逐步写入。',
            phase: 'generate',
            percent: listingProgressPercent(st, startedAt),
          });
        },
      });
      if (
        getState('activeListingGenerationTaskId') !== start.taskId
        || String(getState('batchJobId') || '') !== String(jobId)
      ) {
        throw new Error('已忽略较早的生成任务结果');
      }
    } else {
      data = start;
    }

    const partialErrors = applyGenerateAllResult(data, jobId, {
      getState,
      setStateBulk,
      sectionLabel,
      scheduleStep3AutoSave,
    });
    clearSubmittedTask = Boolean(submittedTaskId);
    const mergeCount = data.mergeMeta?.competitorCount || snapshotAsins?.length || 0;
    if (partialErrors.length > 0) {
      renderListingProgress({
        title: 'Listing 已生成可用内容',
        detail: '系统已保留本轮可用字段，未覆盖现有内容。',
        footer: '可直接编辑当前结果，或再次生成一版。',
        tone: 'success',
        phase: 'done',
        percent: 100,
      });
      showToast('Listing 已生成可用内容，当前结果已保留。', {
        id: 'listing-generation-toast',
        type: 'success',
        duration: 4200,
      });
      hideListingProgress(2200);
    } else if (mergeCount > 1) {
      renderListingProgress({
        title: 'Listing 生成完成',
        detail: `已综合 ${mergeCount} 个竞品，生成完成`,
        footer: '结果已写入下方文案区域。',
        tone: 'success',
        phase: 'done',
        percent: 100,
      });
      showToast('Listing 已生成，点击 Step 3 可查看结果。', {
        id: 'listing-generation-toast',
        type: 'success',
        duration: 3200,
      });
      hideListingProgress(1800);
    } else {
      renderListingProgress({
        title: 'Listing 生成完成',
        detail: '标题、五点、描述和搜索词已生成。',
        footer: '结果已写入下方文案区域。',
        tone: 'success',
        phase: 'done',
        percent: 100,
      });
      showToast('Listing 已生成，点击 Step 3 可查看结果。', {
        id: 'listing-generation-toast',
        type: 'success',
        duration: 3200,
      });
      hideListingProgress(1800);
    }
  } catch (err) {
    // The user switched to another independent product while this request was
    // in flight. The old task must end silently and must not alter the new UI.
    if (String(getState('batchJobId') || '') !== String(jobId)) return;
    const activeTaskId = submittedTaskId || getState('activeListingGenerationTaskId');
    const terminalTaskFailure = isTerminalListingGenerationTaskError(err);
    clearSubmittedTask = Boolean(submittedTaskId && terminalTaskFailure);
    const taskCanRecover = Boolean(activeTaskId && !terminalTaskFailure);
    const friendlyError = taskCanRecover
      ? {
          message: '与生成任务的连接暂时中断，后台任务仍可恢复。',
          footer: `任务参考：${activeTaskId}。刷新当前页面后系统会继续轮询并写回结果。`,
        }
      : formatListingGenerationError(err, { taskId: activeTaskId });
    console.error('[Step3] Listing generation failed:', err);
    if (document.getElementById('step3-generation-progress')) {
      renderListingProgress({
        title: taskCanRecover ? 'Listing 任务仍在后台运行' : '本轮生成已结束，当前内容已保留',
        detail: friendlyError.message,
        footer: friendlyError.footer,
        ...(taskCanRecover ? {} : { tone: 'success' }),
        phase: 'generate',
        percent: taskCanRecover ? 72 : 100,
      });
    } else if (progressContainer) {
      const prompt = document.createElement('div');
      prompt.className = 'empty-generation-prompt';
      const message = document.createElement('p');
      message.textContent = friendlyError.message;
      const footer = document.createElement('p');
      footer.className = 'muted';
      footer.textContent = friendlyError.footer;
      const icon = document.createElement('div');
      icon.className = 'empty-prompt-icon';
      icon.textContent = '✓';
      prompt.append(icon, message, footer);
      progressContainer.replaceChildren(prompt);
    }
    showToast(`Listing：${friendlyError.message}`, {
      id: 'listing-generation-toast',
      type: taskCanRecover ? 'info' : 'success',
      duration: 5000,
      maxWidth: 420,
    });
  } finally {
    if (
      clearSubmittedTask
      && submittedTaskId
      && getState('activeListingGenerationTaskId') === submittedTaskId
    ) {
      setState('activeListingGenerationTaskId', '');
    }
    if (btn) {
      btn.disabled = false;
      btn.textContent = '生成完成';
      setTimeout(() => {
        if (btn) btn.textContent = '生成 Listing';
      }, 1500);
    }
    await refreshListingQueueHint();
  }
}

async function resumeActiveListingGeneration() {
  const jobId = getState('batchJobId');
  if (!jobId) return;
  let recoveredTaskId = getState('activeListingGenerationTaskId') || '';
  let clearRecoveredTask = false;

  try {
    let task = recoveredTaskId ? { id: recoveredTaskId, status: 'recovering' } : null;
    if (!task) {
      const active = await getActiveGenerateAll(jobId);
      if (active?.batchJobId && String(active.batchJobId) !== String(jobId)) {
        throw Object.assign(new Error('恢复任务与当前商品任务不匹配，已停止写回。'), {
          code: 'LISTING_GENERATION_SCOPE_MISMATCH',
          status: 'scope_mismatch',
          taskId: active?.id || active?.task?.id || '',
        });
      }
      task = active?.task;
    }
    if (!task?.id) return;
    const recoverableStatuses = new Set(['queued', 'running', 'completed', 'complete', 'partial', 'recovering']);
    if (!recoverableStatuses.has(String(task.status || '').toLowerCase())) return;
    recoveredTaskId = task.id;
    setState('activeListingGenerationTaskId', recoveredTaskId);

    const startedAt = Date.now();
    renderListingProgress({
      title: '已恢复 Listing 生成任务',
      detail: '系统正在继续处理当前 Listing。',
      footer: '完成后会自动写入标题、五点、描述和搜索词。',
      phase: task?.status === 'queued' ? 'queued' : 'generate',
      percent: listingProgressPercent(task || {}, startedAt),
    });
    const data = await pollGenerateAll(recoveredTaskId, {
      expectedBatchJobId: jobId,
      onProgress(st) {
        if (
          getState('activeListingGenerationTaskId') !== recoveredTaskId
          || String(getState('batchJobId') || '') !== String(jobId)
        ) return;
        const { phaseLabel, detail } = resolveGenerationText(st, startedAt);
        renderListingProgress({
          title: `正在生成 Listing（${phaseLabel}）`,
          detail: detail || '系统正在处理当前 Listing。',
          footer: '完成后会自动写入标题、五点、描述和搜索词。',
          phase: st.status === 'queued' ? 'queued' : 'generate',
          percent: listingProgressPercent(st, startedAt),
        });
      },
    });
    if (
      getState('activeListingGenerationTaskId') !== recoveredTaskId
      || String(getState('batchJobId') || '') !== String(jobId)
    ) return;
    applyGenerateAllResult(data, jobId, {
      getState,
      setStateBulk,
      sectionLabel,
      scheduleStep3AutoSave,
    });
    clearRecoveredTask = true;
    renderListingProgress({
      title: 'Listing 已生成',
      detail: '已恢复并写入本轮结果。',
      footer: '结果已写入下方文案区域。',
      tone: 'success',
      phase: 'done',
      percent: 100,
    });
    hideListingProgress(1800);
    showToast('已恢复并完成 Listing 生成。', {
      id: 'listing-generation-toast',
      type: 'success',
      duration: 3200,
    });
  } catch (err) {
    if (String(getState('batchJobId') || '') !== String(jobId)) return;
    console.warn('[Step3] Active generation recovery did not produce a new result.', err);
    const terminalTaskFailure = isTerminalListingGenerationTaskError(err);
    clearRecoveredTask = Boolean(recoveredTaskId && terminalTaskFailure);
    const taskCanRecover = Boolean(recoveredTaskId && !terminalTaskFailure);
    const friendlyError = taskCanRecover
      ? {
          message: '恢复连接暂时中断，后台任务与恢复凭据均已保留。',
          footer: `任务参考：${recoveredTaskId}。稍后刷新当前页面即可继续恢复。`,
        }
      : formatListingGenerationError(err, { taskId: recoveredTaskId });
    renderListingProgress({
      title: taskCanRecover ? 'Listing 任务仍可恢复' : '恢复任务已结束，当前内容已保留',
      detail: friendlyError.message,
      footer: friendlyError.footer,
      ...(taskCanRecover ? {} : { tone: 'success' }),
      phase: 'generate',
      percent: taskCanRecover ? 72 : 100,
    });
  } finally {
    if (
      clearRecoveredTask
      && recoveredTaskId
      && getState('activeListingGenerationTaskId') === recoveredTaskId
    ) {
      setState('activeListingGenerationTaskId', '');
    }
    await refreshListingQueueHint();
  }
}

// ── Module Init ─────────────────────────────────────────────────

export function initStep3() {
  bindEvents({ generateAllFromBatch, retrySearchTeamFromTask });
  bindStateSubscriptions();
  refreshListingQueueHint();
  void resumeActiveListingGeneration();
}
